import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Plus, Trash, PencilSimple, X, BookBookmark, Copy } from "@phosphor-icons/react";
import { copyText } from "@/lib/clipboard";

const EMPTY = { prompt: "", situation: "", task: "", action: "", result: "", tags: [] };

export default function PersonalExamples() {
  const [items, setItems] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tagsText, setTagsText] = useState("");

  const load = async () => {
    const [exRes, pRes] = await Promise.all([
      api.get("/personal-examples"),
      api.get("/personal-examples/prompts"),
    ]);
    setItems(exRes.data);
    setPrompts(pRes.data.prompts || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = (prompt = "") => {
    setEditing("new");
    setForm({ ...EMPTY, prompt });
    setTagsText("");
  };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ ...EMPTY, ...it });
    setTagsText((it.tags || []).join(", "));
  };

  const save = async () => {
    if (!form.prompt.trim()) return toast.error("Prompt required");
    const payload = {
      ...form,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") await api.post("/personal-examples", payload);
      else await api.put(`/personal-examples/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Couldn't save");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/personal-examples/${id}`);
    load();
  };

  const copyExample = (it) => {
    const star = `Situation: ${it.situation}\nTask: ${it.task}\nAction: ${it.action}\nResult: ${it.result}`;
    copyText(star, "STAR example copied");
  };

  return (
    <AppShell title="Personal Bank">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mb-6">
        <div>
          <p className="text-[var(--brand-muted)] max-w-2xl">
            your reusable bank of STAR stories. interviewers and applications keep asking for these — paste once, win interviews forever.
            also used by Vault Coach to personalise cover-letter rewrites and interview prep.
          </p>
        </div>
        <button onClick={() => openNew()} data-testid="example-add"
          className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 flex items-center gap-2 self-start">
          <Plus size={16} weight="bold"/> new example
        </button>
      </div>

      {/* Prompt chips */}
      <div className="card-hard rounded-md p-4 bg-[var(--pastel-yellow)] mb-6">
        <p className="label-tiny mb-2">/quick prompts — click to start writing</p>
        <div className="flex flex-wrap gap-2" data-testid="example-prompts">
          {prompts.map((p) => (
            <button key={p} onClick={() => openNew(p)} data-testid={`prompt-${p.slice(0,18)}`}
              className="btn-hard bg-white rounded-full px-3 py-1.5 text-xs">
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)] mb-6" data-testid="example-editor">
          <div className="flex items-center justify-between mb-4">
            <p className="label-tiny">/{editing === "new" ? "new example" : "edit"}</p>
            <button onClick={() => setEditing(null)} data-testid="example-cancel"><X size={18}/></button>
          </div>
          <label className="label-tiny block mb-1.5">Prompt / interview question</label>
          <input data-testid="ex-prompt" value={form.prompt} onChange={(e) => setForm({...form, prompt: e.target.value})}
            placeholder="e.g. Tell me about a time you led a team"
            className="w-full card-hard rounded-md px-3 py-2 bg-white mb-4"/>
          {["situation","task","action","result"].map((k) => (
            <div key={k} className="mb-3">
              <label className="label-tiny block mb-1.5">{k} — {k === "situation" ? "the context" : k === "task" ? "what you needed to do" : k === "action" ? "what you actually did" : "the measurable outcome"}</label>
              <textarea data-testid={`ex-${k}`} value={form[k] || ""}
                onChange={(e) => setForm({...form, [k]: e.target.value})}
                placeholder={k === "result" ? "quantify if you can — 'shipped 3 features ahead of deadline'" : ""}
                className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[60px]"/>
            </div>
          ))}
          <label className="label-tiny block mb-1.5">Tags (comma-separated)</label>
          <input data-testid="ex-tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)}
            placeholder="leadership, resilience, data"
            className="w-full card-hard rounded-md px-3 py-2 bg-white mb-4"/>
          <div className="flex gap-2">
            <button onClick={save} data-testid="ex-save"
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm">save example</button>
            <button onClick={() => setEditing(null)} className="btn-hard bg-white rounded-md px-4 py-2 text-sm">cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="card-hard rounded-md p-10 bg-white text-center">
          <BookBookmark size={36} weight="duotone" className="mx-auto mb-3 text-[var(--brand-muted)]"/>
          <p className="font-display text-2xl tracking-tight">no examples yet.</p>
          <p className="text-sm text-[var(--brand-muted)] mt-2">pick a prompt above or write your first STAR story.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="examples-list">
          {items.map((it) => (
            <div key={it.id} className="card-hard rounded-md p-5 bg-white" data-testid={`example-${it.id}`}>
              <p className="label-tiny mb-1.5">/{(it.tags||[]).join(' · ') || "untagged"}</p>
              <h3 className="font-display text-lg tracking-tight mb-3">{it.prompt}</h3>
              <div className="space-y-1.5 text-sm text-[var(--brand-muted)]">
                {it.situation && <p><b className="text-[var(--brand-ink)]">S:</b> {it.situation}</p>}
                {it.task && <p><b className="text-[var(--brand-ink)]">T:</b> {it.task}</p>}
                {it.action && <p><b className="text-[var(--brand-ink)]">A:</b> {it.action}</p>}
                {it.result && <p><b className="text-[var(--brand-ink)]">R:</b> {it.result}</p>}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => copyExample(it)} data-testid={`copy-${it.id}`}
                  className="btn-hard bg-white rounded-md px-2.5 py-1 text-xs flex items-center gap-1">
                  <Copy size={12}/> copy STAR
                </button>
                <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}
                  className="btn-hard bg-white rounded-md px-2.5 py-1 text-xs flex items-center gap-1">
                  <PencilSimple size={12}/> edit
                </button>
                <button onClick={() => remove(it.id)} data-testid={`del-${it.id}`}
                  className="btn-hard bg-white rounded-md px-2.5 py-1 text-xs flex items-center gap-1">
                  <Trash size={12}/> delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
